import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { logException } from 'approot/shared/debug';
import {
  PRINTABLES_LIST,
  SHEETS_LIST,
  ASSESSMENT_FOR_LEARNING_TOOL_LIST,
  SHEET_GROUP_MAIN,
  SHEET_GROUP_PRINTABLES,
  SheetGroup,
  DocumentData,
  SheetPreviewRatio,
  LessonSheets,
  LessonSheetData,
  SHEET_GROUP_RUBRICS,
  LessonPrintableData,
  AssessmentDocumentData,
  AssessmentRubricsData,
} from './lesson.constants';
import { LessonType } from 'webclient.constants';
import {
  AE_RUBRIC_DOCUMENT,
  ASSESSMENT,
  PDF_FORMAT,
  SOCIAL_AND_EMOTIONAL_DOCUMENT,
  UNIT_RUBRIC_DOCUMENT,
  VICTORIAN_RUBRIC_DOCUMENT,
} from 'global/constants';
import { LessonWorksheetFragment_worksheet_document } from './sections/lesson-worksheets/__generated__/LessonWorksheetFragment';
import {
  AssessmentWorksheetsFragment_assessmentDoc_document,
  AssessmentWorksheetsFragment_selfAssessment_document,
} from 'approot/assessment/sections/worksheets/__generated__/AssessmentWorksheetsFragment';
import { StudentLessonWorksheetsFragment_worksheet_document } from 'approot/student-lesson/sections/worksheets/__generated__/StudentLessonWorksheetsFragment';
import { StudentAssessmentWorksheetsFragment_assessmentDoc_document } from 'approot/student-assessment/sections/worksheets/__generated__/StudentAssessmentWorksheetsFragment';
import { LessonPrintablesFragment_printableLessonPlan_document } from './sections/lesson-printables/__generated__/LessonPrintablesFragment';
import {
  AssessmentRubricsFragment_unitRubric_document,
  AssessmentRubricsFragment_aeRubric_document,
  AssessmentRubricsFragment_victorianRubric_document,
} from 'approot/assessment/sections/rubrics/__generated__/AssessmentRubricsFragment';
import { AssessmentForLearningToolFragment_assessmentForLearningTool_document } from 'approot/assessment/sections/assessment-for-learning-tool/__generated__/AssessmentForLearningToolFragment';
import { getUnitLabel } from 'approot/shared/logic/content-label';
import { useRegionData } from 'approot/shared/region/region.apollo';
import {
  EVENT_CATEGORY_AE_RUBRIC,
  EVENT_CATEGORY_RUBRIC,
  EVENT_CATEGORY_SOCIAL_EMOTIONAL_RUBRIC,
  EVENT_CATEGORY_VIC_RUBRIC,
} from 'approot/shared/data-tracker/data-events.constants';
import {
  GetLessonById_getLessonById,
  GetLessonById_getLessonById_data,
  GetLessonById_getLessonById_focusQuestion,
} from './__generated__/GetLessonById';
import { GetGuestLessonById_getLessonById } from './__generated__/GetGuestLessonById';
import { hasRichtextContent } from 'lib/form/richtextarea/richtextarea.utils';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';

type RatioUrl = { group: SheetGroup; url: string };

const findSheetImgRatioUrl = (
  lesson: LessonSheetData,
  unitLabel: string
): RatioUrl[] => {
  const sheetTypes = [...SHEETS_LIST[lesson.type]];
  const printableTypes = [...PRINTABLES_LIST[lesson.type]];
  const rubricTypes = getAssessmentRubricsList(unitLabel);

  const assessmentForLearningToolTypes = [
    ...ASSESSMENT_FOR_LEARNING_TOOL_LIST[ASSESSMENT],
  ];

  return [
    findImgRatioUrl(lesson, sheetTypes, SHEET_GROUP_MAIN),
    findImgRatioUrl(lesson, printableTypes, SHEET_GROUP_PRINTABLES),
    findImgRatioUrl(lesson, rubricTypes, SHEET_GROUP_RUBRICS),
    findImgRatioUrl(lesson, assessmentForLearningToolTypes, SHEET_GROUP_MAIN),
  ];
};

export const getSmallestPagePreviewUrl = (
  document:
    | LessonWorksheetFragment_worksheet_document
    | StudentLessonWorksheetsFragment_worksheet_document
    | LessonPrintablesFragment_printableLessonPlan_document
    | AssessmentWorksheetsFragment_assessmentDoc_document
    | StudentAssessmentWorksheetsFragment_assessmentDoc_document
    | AssessmentWorksheetsFragment_selfAssessment_document
    | AssessmentRubricsFragment_unitRubric_document
    | AssessmentRubricsFragment_aeRubric_document
    | AssessmentRubricsFragment_victorianRubric_document
    | AssessmentForLearningToolFragment_assessmentForLearningTool_document
    | undefined
    | null
): string | null | undefined => {
  return (
    document &&
    document.individualPages &&
    document.individualPages[0] &&
    document.individualPages[0].pagesPreview &&
    document.individualPages[0].pagesPreview[0] &&
    document.individualPages[0].pagesPreview[0]?.urls?.[0]?.url
  );
};

const findImgRatioUrl = (
  lesson: LessonSheetData,
  sheetTypes: DocumentData[],
  group: SheetGroup
) => {
  sheetTypes.sort((a, b) => {
    if (a.displaySingle && !b.displaySingle) return 1;
    if (!a.displaySingle && b.displaySingle) return 1;
    return 0;
  });
  const sheetType = sheetTypes.find(type => lesson[type.key]);
  const url =
    sheetType &&
    lesson[sheetType.key] &&
    getSmallestPagePreviewUrl(lesson[sheetType.key]!.document);

  return {
    group,
    url: url || '',
  };
};

export const getSheetAspectRatios = (
  lesson: LessonSheetData,
  unitLabel: string
) => {
  const urls = findSheetImgRatioUrl(lesson, unitLabel);
  return urls.reduce(
    (promise, { group, url }) =>
      promise.then(
        (ratios: SheetPreviewRatio) =>
          new Promise((resolve, reject) => {
            if (!url) return resolve(ratios);
            const img = new Image();
            img.addEventListener(
              'load',
              () => {
                const value = (img.height / img.width) * 100;
                ratios[group] = value;
                return resolve(ratios);
              },
              false
            );
            img.addEventListener(
              'error',
              (e: Event) => {
                logException({
                  sourceFile: 'lesson.utils.tsx',
                  message: 'Problem loading image',
                  e,
                });
                return resolve(ratios);
              },
              false
            );
            img.src = url;
          })
      ),
    Promise.resolve({})
  );
};

export const useSheetPreviewData = (
  contentType: LessonType,
  contentId: number,
  sheets?: LessonSheets | null
): LessonSheetData => {
  const { t } = useTranslation();

  const [ratios, setRatios] = useState<SheetPreviewRatio | undefined>();

  let data: LessonSheetData = useMemo(
    () => ({
      id: contentId,
      type: contentType,
      ...sheets,
    }),
    [contentId, contentType, sheets]
  );

  if (ratios) {
    data.sheetPreviewRatio = ratios;
  }

  const getAspectRatios = useCallback(async () => {
    const ratios = await getSheetAspectRatios(data, getUnitLabel(t));
    setRatios(ratios);
  }, [data, t]);

  if (!ratios) {
    getAspectRatios();
  }

  useEffect(() => {
    // each time we view a new lesson recalculate the ratio
    if (ratios) {
      setRatios(undefined);
    }
    //eslint-disable-next-line
  }, [contentId]);

  return data;
};

export const useFilterDocumentsByCurrentRegion = <
  T extends LessonPrintableData | AssessmentDocumentData
>(
  sheetList: T[]
) => {
  const { region } = useRegionData();

  return useMemo(
    () =>
      sheetList.filter(
        sheetDef =>
          !('regions' in sheetDef) ||
          !sheetDef.regions ||
          sheetDef.regions.includes(region)
      ),
    [sheetList, region]
  );
};

export function hasNotes(
  lesson: GetLessonById_getLessonById | GetGuestLessonById_getLessonById
) {
  return hasRichtextContent(lesson.notesJson);
}

export function hasFocusOrPhenomenaQuestion({
  question,
  activitiesQuestion,
  isActivitiesPublished,
}: {
  isActivitiesPublished: boolean;
  question: GetLessonById_getLessonById_focusQuestion | null;
  activitiesQuestion?: GetLessonById_getLessonById_focusQuestion | null;
}) {
  if (isActivitiesPublished && !isRichtextEmpty(activitiesQuestion?.value)) {
    return true;
  }
  return hasRichtextContent(question?.value);
}

export function hasVocabulary(
  data: GetLessonById_getLessonById_data | null
): boolean {
  if (data) {
    return !!(data && data.vocabularies && data.vocabularies.length > 0);
  }
  return false;
}

export function hasPrintables(
  lesson:
    | GetLessonById_getLessonById
    | LessonSheetData
    | GetGuestLessonById_getLessonById,
  displayAnswerSheet: boolean = true
): boolean {
  return !!(
    lesson &&
    (lesson.printableLessonPlan ||
      (lesson.printableAnswerSheet && displayAnswerSheet) ||
      lesson.printableWorksheet ||
      lesson.printableSupportSheet ||
      lesson.printableExtensionSheet ||
      lesson.printableVocabularyEnSheet ||
      lesson.printableVocabularyEsSheet ||
      lesson.printableTeacherInformationSheet ||
      lesson.printableLessonRubricSheet)
  );
}

export function hasWorksheet(
  lesson: GetLessonById_getLessonById | GetGuestLessonById_getLessonById
): lesson is GetLessonById_getLessonById & {
  worksheet: { document: { downloadUrl: string } };
} {
  return !!(lesson && lesson.worksheet && lesson.worksheet.document);
}

export function getWorksheetDownloadUrl(lesson: GetLessonById_getLessonById) {
  if (hasWorksheet(lesson)) {
    return lesson.worksheet.document.downloadURL;
  }
}

export function getAssessmentRubricsList(
  unitLabel: string
): AssessmentRubricsData[] {
  return [
    {
      key: 'unitRubric',
      name: `${unitLabel} Rubric`,
      dataEvent: EVENT_CATEGORY_RUBRIC,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: `Are you sure you want to delete this ${unitLabel} Rubric?`,
      deleteAlertTextSecondary: `Any shared links will be broken until a new ${unitLabel} Rubric is added.`,
      placeholderText: `Drag and drop a ${unitLabel} Rubric here to upload it.`,
      documentType: UNIT_RUBRIC_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: false,
    },
    {
      key: 'aeRubric',
      name: 'A-E Rubric',
      dataEvent: EVENT_CATEGORY_AE_RUBRIC,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: 'Are you sure you want to delete this A-E Rubric?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new A-E Rubric is added.',
      placeholderText: 'Drag and drop an A-E Rubric here to upload it.',
      documentType: AE_RUBRIC_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: false,
    },
    {
      key: 'victorianRubric',
      name: 'Victorian Rubric',
      dataEvent: EVENT_CATEGORY_VIC_RUBRIC,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: 'Are you sure you want to delete this Victorian Rubric?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Victorian Rubric is added.',
      placeholderText: 'Drag and drop a Victorian Rubric here to upload it.',
      documentType: VICTORIAN_RUBRIC_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: false,
    },
    {
      key: 'socialAndEmotionalRubric',
      name: 'Social and Emotional Rubric',
      dataEvent: EVENT_CATEGORY_SOCIAL_EMOTIONAL_RUBRIC,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText:
        'Are you sure you want to delete this Social and Emotional Rubric?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Social and Emotional Rubric is added.',
      placeholderText:
        'Drag and drop a Social and Emotional Rubric here to upload it.',
      documentType: SOCIAL_AND_EMOTIONAL_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: false,
    },
  ];
}
